import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Empty,
  Input,
  Modal,
  Space,
  Spin,
  Tooltip,
  Typography,
  message as antdMessage,
} from "antd";
import {
  DeleteOutlined,
  MessageOutlined,
  PlusOutlined,
  RobotOutlined,
  SendOutlined,
  UserOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  askChat,
  deleteChatSession,
  getChatSession,
  listChatSessions,
} from "../../services/chat";
import type {
  IChatMessage,
  IChatSessionSummary,
} from "../types/chat.types";

const { Text, Title } = Typography;
const { TextArea } = Input;

const SIDEBAR_WIDTH = 280;

const ChatComponent = () => {
  const [sessions, setSessions] = useState<IChatSessionSummary[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const fetchSessions = useCallback(async () => {
    try {
      setSessionsLoading(true);
      const data = await listChatSessions();
      setSessions(data);
    } catch {
      antdMessage.error("Failed to load chat sessions");
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const openSession = useCallback(async (sessionId: string) => {
    setActiveSessionId(sessionId);
    setMessages([]);
    try {
      setMessagesLoading(true);
      const session = await getChatSession(sessionId);
      setMessages(session.messages ?? []);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        antdMessage.error("Session not found");
      } else {
        antdMessage.error("Failed to load session");
      }
      setActiveSessionId(null);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  const startNewChat = useCallback(() => {
    setActiveSessionId(null);
    setMessages([]);
    setQuestion("");
  }, []);

  const handleDelete = useCallback(
    (session: IChatSessionSummary) => {
      Modal.confirm({
        title: "Delete chat?",
        content: `Delete "${session.title || "Untitled chat"}"? This cannot be undone.`,
        okText: "Delete",
        okType: "danger",
        onOk: async () => {
          try {
            await deleteChatSession(session.sessionId);
            antdMessage.success("Chat deleted");
            setSessions((prev) =>
              prev.filter((s) => s.sessionId !== session.sessionId)
            );
            if (activeSessionId === session.sessionId) {
              startNewChat();
            }
          } catch {
            antdMessage.error("Failed to delete chat");
          }
        },
      });
    },
    [activeSessionId, startNewChat]
  );

  const handleSend = useCallback(async () => {
    const trimmed = question.trim();
    if (!trimmed || asking) return;

    const optimisticUser: IChatMessage = {
      messageId: `temp-${Date.now()}`,
      role: "user",
      content: trimmed,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimisticUser]);
    setQuestion("");
    setAsking(true);

    try {
      const res = await askChat(trimmed, activeSessionId ?? undefined);
      setMessages((prev) => {
        const withoutTemp = prev.filter(
          (m) => m.messageId !== optimisticUser.messageId
        );
        return [
          ...withoutTemp,
          {
            messageId: res.userMessageId,
            role: "user",
            content: trimmed,
            createdAt: res.createdAt,
          },
          {
            messageId: res.messageId,
            role: "assistant",
            content: res.answer,
            createdAt: res.createdAt,
          },
        ];
      });
      if (!activeSessionId) {
        setActiveSessionId(res.sessionId);
      }
      fetchSessions();
    } catch (error: any) {
      setMessages((prev) =>
        prev.filter((m) => m.messageId !== optimisticUser.messageId)
      );
      setQuestion(trimmed);
      const serverMsg = error?.response?.data?.message;
      if (error?.code === "ECONNABORTED") {
        antdMessage.error("The request timed out. Please try again.");
      } else {
        antdMessage.error(serverMsg || "Failed to get a response");
      }
    } finally {
      setAsking(false);
    }
  }, [question, asking, activeSessionId, fetchSessions]);

  useLayoutEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, asking]);

  const sortedSessions = useMemo(() => {
    return [...sessions].sort(
      (a, b) =>
        moment(b.lastMessageAt || b.updatedAt).valueOf() -
        moment(a.lastMessageAt || a.updatedAt).valueOf()
    );
  }, [sessions]);

  return (
    <div
      style={{
        height: "calc(100vh - 64px - 56px)",
        display: "flex",
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      {/* ─── Sessions sidebar ───────────────────────── */}
      <div
        style={{
          width: SIDEBAR_WIDTH,
          borderRight: "1px solid #e2e8f0",
          display: "flex",
          flexDirection: "column",
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            padding: 16,
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            block
            onClick={startNewChat}
          >
            New Chat
          </Button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 8 }}>
          {sessionsLoading ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <Spin />
            </div>
          ) : sortedSessions.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                No chats yet
              </Text>
            </div>
          ) : (
            sortedSessions.map((s) => {
              const isActive = s.sessionId === activeSessionId;
              return (
                <div
                  key={s.sessionId}
                  onClick={() => openSession(s.sessionId)}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 8,
                    cursor: "pointer",
                    marginBottom: 4,
                    background: isActive ? "#e0f2fe" : "transparent",
                    border: `1px solid ${isActive ? "#7dd3fc" : "transparent"}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive)
                      (e.currentTarget as HTMLDivElement).style.background =
                        "#f1f5f9";
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive)
                      (e.currentTarget as HTMLDivElement).style.background =
                        "transparent";
                  }}
                >
                  <MessageOutlined
                    style={{ color: "#64748b", fontSize: 14, flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#0f172a",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {s.title || "Untitled chat"}
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                      {moment(s.lastMessageAt || s.updatedAt).fromNow()}
                    </div>
                  </div>
                  <Tooltip title="Delete chat">
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(s);
                      }}
                    />
                  </Tooltip>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ─── Message thread ─────────────────────────── */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 24,
            background: "#fff",
          }}
        >
          {messagesLoading ? (
            <div style={{ textAlign: "center", padding: 60 }}>
              <Spin />
            </div>
          ) : messages.length === 0 ? (
            <div
              style={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Empty
                image={
                  <RobotOutlined
                    style={{ fontSize: 56, color: "#cbd5e1" }}
                  />
                }
                imageStyle={{ height: 60 }}
                description={
                  <div>
                    <Title level={5} style={{ color: "#64748b", margin: 0 }}>
                      Ask anything about your operations
                    </Title>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      e.g. "How many trucks came in today?"
                    </Text>
                  </div>
                }
              />
            </div>
          ) : (
            <Space
              direction="vertical"
              size={16}
              style={{ width: "100%", maxWidth: 860, margin: "0 auto" }}
            >
              {messages.map((m) => (
                <MessageBubble key={m.messageId} message={m} />
              ))}
              {asking && (
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <Avatar role="assistant" />
                  <div
                    style={{
                      padding: "10px 14px",
                      background: "#f1f5f9",
                      borderRadius: 12,
                      color: "#64748b",
                      fontSize: 13,
                    }}
                  >
                    <Spin size="small" /> <span style={{ marginLeft: 8 }}>Thinking...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </Space>
          )}
        </div>

        {/* ─── Composer ───────────────────────────── */}
        <div
          style={{
            borderTop: "1px solid #e2e8f0",
            padding: 16,
            background: "#fff",
          }}
        >
          <div style={{ maxWidth: 860, margin: "0 auto" }}>
            <Space.Compact style={{ width: "100%" }}>
              <TextArea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Ask a question..."
                autoSize={{ minRows: 1, maxRows: 6 }}
                disabled={asking}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                style={{ resize: "none" }}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSend}
                loading={asking}
                disabled={!question.trim()}
              >
                Send
              </Button>
            </Space.Compact>
            <Text
              type="secondary"
              style={{ fontSize: 11, display: "block", marginTop: 6 }}
            >
              Press Enter to send, Shift+Enter for a new line
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

const Avatar = ({ role }: { role: "user" | "assistant" }) => (
  <div
    style={{
      width: 32,
      height: 32,
      borderRadius: "50%",
      background: role === "user" ? "#1677ff" : "#8b5cf6",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      fontSize: 14,
    }}
  >
    {role === "user" ? <UserOutlined /> : <RobotOutlined />}
  </div>
);

const MessageBubble = ({ message }: { message: IChatMessage }) => {
  const isUser = message.role === "user";
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        flexDirection: isUser ? "row-reverse" : "row",
      }}
    >
      <Avatar role={message.role} />
      <div style={{ maxWidth: "75%" }}>
        <div
          style={{
            padding: "10px 14px",
            background: isUser ? "#1677ff" : "#f1f5f9",
            color: isUser ? "#fff" : "#0f172a",
            borderRadius: 12,
            fontSize: 14,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            lineHeight: 1.5,
          }}
        >
          {message.content}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "#94a3b8",
            marginTop: 4,
            textAlign: isUser ? "right" : "left",
          }}
        >
          {moment(message.createdAt).format("MMM D, h:mm A")}
        </div>
      </div>
    </div>
  );
};

export default ChatComponent;
